import { ref, computed } from 'vue'

// ─────────────────────────────────────────────────────────────────────────────
// useChatNavigation — utilities untuk navigasi pertanyaan (Q1, Q2, …) di Chat AI.
//
// Tanggung jawab:
//   • Track read-progress (0–100) berdasar scroll
//   • Tandai apakah user sedang scroll ke atas (userScrolledUp)
//   • Map id pesan → nomor Q (qIndexById)
//   • Lompat ke Q tertentu (scrollToQ) dengan pulse highlight
//   • Lompat relatif ±1 berdasar Q yang sedang terlihat (jumpQ)
//
// Composable ini sengaja dipisah dari ChatAI.vue untuk menjaga ukuran komponen
// tetap di bawah hard-limit (800 baris).
// ─────────────────────────────────────────────────────────────────────────────

const PULSE_DURATION_MS = 1500
const SPY_TOP_OFFSET    = 90 // px dari top scroll-container untuk anchor "current Q"

export function useChatNavigation({ messagesEl, messages, newMessageCount }) {
  const userScrolledUp = ref(false)
  const scrollProgress = ref(0)
  const pulseMessageId = ref(null)
  let pulseTimer = null

  // Pemetaan id-pesan → nomor Q (1-based). Hanya pesan role=user yang valid
  // dapat nomor — pesan error/streaming kosong dilewati supaya nomor tetap stabil.
  const qIndexById = computed(() => {
    const map = new Map()
    let n = 0
    for (const m of messages.value) {
      if (m && m.role === 'user' && m.content && !m.error) {
        n++
        map.set(m.id, n)
      }
    }
    return map
  })

  function onScroll() {
    const el = messagesEl.value
    if (!el) return
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight
    const wasScrolled = userScrolledUp.value
    userScrolledUp.value = dist > 80
    if (!userScrolledUp.value && wasScrolled && newMessageCount) {
      newMessageCount.value = 0
    }
    const range = el.scrollHeight - el.clientHeight
    scrollProgress.value = range > 0
      ? Math.min(100, Math.max(0, (el.scrollTop / range) * 100))
      : 0
  }

  function pulseQ(qIndex) {
    let count = 0
    for (const m of messages.value) {
      if (m.role === 'user' && m.content && !m.error) count++
      if (count === qIndex && m.role === 'user') {
        pulseMessageId.value = m.id
        if (pulseTimer) clearTimeout(pulseTimer)
        pulseTimer = setTimeout(() => { pulseMessageId.value = null }, PULSE_DURATION_MS)
        break
      }
    }
  }

  function scrollToQ(qIndex) {
    const el = messagesEl.value?.querySelector(`[data-qid="${qIndex}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    pulseQ(qIndex)
  }

  // Cari Q yang sedang aktif (anchor paling atas yang masih di atas garis trigger),
  // lalu lompat ±delta (clamped). Dipakai oleh shortcut `[` dan `]`.
  function jumpQ(delta) {
    const root = messagesEl.value
    if (!root) return
    const anchors = Array.from(root.querySelectorAll('[data-qid]'))
    if (!anchors.length) return
    const ids = anchors
      .map(el => parseInt(el.getAttribute('data-qid') || '0', 10))
      .filter(n => n > 0)
      .sort((a, b) => a - b)
    if (!ids.length) return

    const containerTop = root.getBoundingClientRect().top
    const trigger = containerTop + SPY_TOP_OFFSET
    let current = ids[0]
    for (const el of anchors) {
      if (el.getBoundingClientRect().top <= trigger) {
        const qid = parseInt(el.getAttribute('data-qid') || '0', 10)
        if (qid > 0) current = qid
      }
    }
    const idx = ids.indexOf(current)
    const target = ids[Math.min(ids.length - 1, Math.max(0, idx + delta))]
    scrollToQ(target)
  }

  function disposeNavigation() {
    if (pulseTimer) clearTimeout(pulseTimer)
    pulseTimer = null
  }

  return {
    userScrolledUp,
    scrollProgress,
    pulseMessageId,
    qIndexById,
    onScroll,
    scrollToQ,
    jumpQ,
    disposeNavigation,
  }
}
